import { Box, Group, SimpleGrid, Text, Tooltip } from '@mantine/core';
import { Info } from 'lucide-react';
import type { ReactElement } from 'react';

export interface StatItem {
    label: string;
    value: string;
    hint?: string;
    /** Optional explanation shown via an ⓘ tooltip next to the label. */
    info?: string;
    /** Optional short pill shown at the tile's top-right (e.g. a cohort rank like "#3 of 11"). */
    badge?: string;
    /** Optional CSS color for the value (e.g. a status token) — defaults to the text color. */
    valueColor?: string;
}

interface StatBandProps {
    stats: StatItem[];
}

/**
 * A row of compact metric tiles used at the top of the L1 and L2 levels. Each
 * stat is its own separated card (radius + shadow from the active theme), so the
 * band reads as a light row of cards rather than one rigid, hard-ruled block.
 */
export default function StatBand({ stats }: StatBandProps): ReactElement {
    return (
        <SimpleGrid cols={{ base: 2, sm: Math.min(stats.length, 5) }} spacing="sm" verticalSpacing="sm">
            {stats.map((stat) => (
                <Box
                    key={stat.label}
                    px="lg"
                    py="md"
                    style={{
                        background: 'var(--bm-color-card)',
                        border: '1px solid var(--bm-color-border)',
                        borderRadius: 'var(--bm-radius)',
                        boxShadow: 'var(--bm-card-shadow)',
                    }}
                >
                    <Group gap={6} align="center" wrap="nowrap" justify="space-between" mih={16}>
                        <Group gap={5} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
                            <Text
                                fw={700}
                                tt="uppercase"
                                style={{
                                    fontSize: '9.5px',
                                    letterSpacing: '0.09em',
                                    color: 'var(--bm-color-text-dimmed)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {stat.label}
                            </Text>
                            {stat.info !== undefined && (
                                <Tooltip label={stat.info} multiline w={220} withArrow position="top">
                                    <Box component="span" aria-label={`About ${stat.label}`} style={{ display: 'inline-flex', cursor: 'help', color: 'var(--bm-color-text-dimmed)' }}>
                                        <Info size={11} />
                                    </Box>
                                </Tooltip>
                            )}
                        </Group>
                        {stat.badge !== undefined && (
                            <Text
                                component="span"
                                fw={700}
                                style={{
                                    flexShrink: 0,
                                    fontSize: '9px',
                                    letterSpacing: '0.02em',
                                    color: 'var(--bm-color-brand-accent)',
                                    background: 'var(--bm-color-brand-soft)',
                                    border: '1px solid var(--bm-color-brand-border)',
                                    borderRadius: '999px',
                                    padding: '1px 7px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {stat.badge}
                            </Text>
                        )}
                    </Group>
                    <Text
                        fw={700}
                        mt={8}
                        style={{ fontSize: '24px', letterSpacing: '-0.02em', lineHeight: 1.15, color: stat.valueColor ?? 'var(--bm-color-text)' }}
                    >
                        {stat.value}
                    </Text>
                    {stat.hint !== undefined && (
                        <Text mt={3} style={{ fontSize: '10.5px', color: 'var(--bm-color-text-dimmed)' }}>
                            {stat.hint}
                        </Text>
                    )}
                </Box>
            ))}
        </SimpleGrid>
    );
}
